/*
`List.js` is a React component that manages and displays a list of customers.

Features:
- Fetching Data: Uses `getCustomers` and `getSellers` to fetch data from the API and update the component's state.
- Lifecycle: Calls the data fetching functions in the `componentDidMount` lifecycle method.
- Data Management: 
  - `onSaveHandler`: Creates or updates a customer, then refreshes the customer list.
  - `onDeleteConfirmation`: Deletes a customer, then refreshes the customer list.
- Data Display: 
  - `getSellerName`: Creates a dictionary of seller names for display.
  - `render`: Returns a `MaterialTable` component displaying the customer list, with actions for adding, saving, and deleting customers.

Dialogs:
- `CreationDialog`: Opens for adding or saving customers.
- `DeleteConfirmationDialog`: Opens for confirming customer deletion.
*/

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import MaterialTable, { MTableToolbar } from 'material-table';
import { Chip, Button } from '@material-ui/core';
import {
  getCustomer,
  createCustomer,
  updateCustomer,
  deleteCustomer,
} from '../../api/customerApi';
import { getSeller } from '../../api/sellerApi';
import { api } from '../../helpers/apiHelper';
import CreationDialog from './Form';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';

export default class CustomerList extends Component {
  state = {
    customers: [],
    sellers: [],
  };
  getCustomers = () => {
    this.setState({ customers: [] });
    getCustomer().then((data) => {
      this.setState({ customers: data });
    });
  };

  getSellers = () => {
    api
      .get('/fastex-sellers')
      .then((res) => this.setState({ sellers: res.data }));
  };

  componentDidMount() {
    this.getCustomers();
    this.getSellers();
  }

  onSaveHandler = (data) => {
    let payLoad = {
      ...data,
    };
    if (data.CustomerId) {
      updateCustomer(payLoad).then((res) => {
        this.getCustomers();
      });
    } else {
      createCustomer(payLoad).then((res) => {
        this.getCustomers();
      });
    }
  };

  onDeleteConfirmation = ({ CustomerId }) => {
    deleteCustomer(CustomerId).then((res) => {
      this.getCustomers();
    });
  };

  getSellerName = () => {
    const sellerDictionary = {};
    this.state.sellers.map((seller) => {
      const { SellerId, SellerName } = seller;
      sellerDictionary[SellerId] = SellerName;
    });
    return sellerDictionary;
  };

  render() {
    return (
      <MaterialTable
        title='Kunder'
        columns={[
          {
            title: 'Namn',
            field: 'CustomerName',
          },
          { title: 'Adress', field: 'BusinessAddress' },
          {
            title: 'Säljkontakt',
            field: 'SellerId',
            lookup: this.getSellerName(),
          },
        ]}
        data={this.state.customers}
        actions={[
          {
            icon: 'add',
            tooltip: 'Add Customer',
            isFreeAction: true,
          },
          {
            icon: 'save',
            tooltip: 'Save Customer',
          },
          {
            icon: 'delete',
            tooltip: 'delete Customer',
            onClick: (event, rowData) => this.onDeleteConfirmation(rowData),
          },
        ]}
        options={{
          actionsColumnIndex: -1,
          pageSize: 20,
        }}
        components={{
          Action: (props) => {
            if (props.action.icon === 'add') {
              return (
                <CreationDialog
                  onSave={this.onSaveHandler}
                  sellers={this.state.sellers}
                ></CreationDialog>
              );
            }
            if (props.action.icon === 'save') {
              return (
                <CreationDialog
                  isEdit={true}
                  customer={props.data}
                  sellers={this.state.sellers}
                  onSave={this.onSaveHandler}
                ></CreationDialog>
              );
            }
            if (props.action.icon === 'delete') {
              return (
                <DeleteConfirmationDialog
                  title='Bekräftelse'
                  message='Är du säker på att du vill radera denna kund?'
                  onConfirmation={(event) =>
                    props.action.onClick(event, props.data)
                  }
                ></DeleteConfirmationDialog>
              );
            } else return null;
          },
        }}
        localization={{
          body: {
            emptyDataSourceMessage: 'Ingen information att visa',
          },
          toolbar: {
            searchTooltip: 'Sök',
            searchPlaceholder: 'Sök',
          },
          pagination: {
            labelRowsSelect: 'Rader',
            labelDisplayedRows: '{from}-{to} av {count}',
          },
          header: {
            actions: '',
          },
        }}
      />
    );
  }
}
